
import React, { useEffect, useRef } from "react";
import "./Styles.css";

const clamp = (value, min, max) =>
    Math.max(min, Math.min(max, value));

export default function SalesInfrastructureField({
    primary = "#21e6c1",
    secondary = "#25b8f4",
    accent = "#a78bfa",
    nodeCount = 58,
    connectionDistance = 155,
    pulseCount = 9,
    maxFps = 30,
    breakpoint = 825,
    className = ""
}) {
    const rootRef = useRef(null);
    const canvasRef = useRef(null);

    useEffect(() => {
        const root = rootRef.current;
        const canvas = canvasRef.current;
        const hero = root?.parentElement;
        const ctx = canvas?.getContext("2d", {
            alpha: true
        });

        if (!root || !hero || !ctx) return;

        const desktopQuery = window.matchMedia(
            `(min-width: ${breakpoint + 1}px)`
        );

        const motionQuery = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
        );

        let width = 0;
        let height = 0;
        let nodes = [];
        let pulses = [];

        let raf = 0;
        let lastFrame = 0;
        let elapsed = 0;
        let active = false;
        let visible = true;

        const pointer = {
            x: -1000,
            y: -1000,
            active: false
        };

        const random = (min, max) =>
            min + Math.random() * (max - min);

        const resize = () => {
            const rect = root.getBoundingClientRect();

            width = rect.width;
            height = rect.height;

            if (!width || !height) return;

            const dpr = Math.min(
                window.devicePixelRatio || 1,
                2
            );

            canvas.width = Math.round(width * dpr);
            canvas.height = Math.round(height * dpr);

            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };

        const createNetwork = () => {
            nodes = Array.from(
                { length: nodeCount },
                (_, index) => ({
                    x: Math.random(),
                    y: Math.random(),
                    phase: random(0, Math.PI * 2),
                    speed: random(0.35, 0.85),
                    radius: random(0.9, 2.1),
                    color: index % 7 === 0
                        ? accent
                        : index % 3 === 0
                            ? secondary
                            : primary
                })
            );

            pulses = Array.from(
                { length: pulseCount },
                () => ({
                    from: Math.floor(random(0, nodeCount)),
                    to: Math.floor(random(0, nodeCount)),
                    progress: Math.random(),
                    speed: random(0.12, 0.3)
                })
            );
        };

        const getPositions = (time) =>
            nodes.map(node => {
                let x =
                    node.x * width +
                    Math.sin(time * node.speed + node.phase) * 16;

                let y =
                    node.y * height +
                    Math.cos(time * node.speed + node.phase) * 12;

                if (pointer.active) {
                    const dx = x - pointer.x;
                    const dy = y - pointer.y;
                    const distance = Math.hypot(dx, dy);

                    if (distance > 0 && distance < 170) {
                        const strength =
                            Math.pow(1 - distance / 170, 2) * 30;

                        x += (dx / distance) * strength;
                        y += (dy / distance) * strength;
                    }
                }

                return { ...node, x, y };
            });

        const draw = (time, dt) => {
            if (!width || !height) return;

            ctx.clearRect(0, 0, width, height);

            const positions = getPositions(time);
            const edges = [];

            // Cursor illumination
            if (pointer.active) {
                const glow = ctx.createRadialGradient(
                    pointer.x,
                    pointer.y,
                    0,
                    pointer.x,
                    pointer.y,
                    230
                );

                glow.addColorStop(
                    0,
                    "rgba(26, 231, 195, 0.12)"
                );

                glow.addColorStop(
                    0.45,
                    "rgba(22, 172, 219, 0.045)"
                );

                glow.addColorStop(
                    1,
                    "rgba(22, 172, 219, 0)"
                );

                ctx.fillStyle = glow;
                ctx.fillRect(0, 0, width, height);
            }

            // Connected infrastructure network
            for (let i = 0; i < positions.length; i++) {
                const a = positions[i];

                for (let j = i + 1; j < positions.length; j++) {
                    const b = positions[j];

                    const distance = Math.hypot(
                        a.x - b.x,
                        a.y - b.y
                    );

                    if (distance > connectionDistance) {
                        continue;
                    }

                    const midpointX = (a.x + b.x) / 2;
                    const midpointY = (a.y + b.y) / 2;

                    const cursorDistance = pointer.active
                        ? Math.hypot(
                            midpointX - pointer.x,
                            midpointY - pointer.y
                        )
                        : Infinity;

                    const hoverBoost = clamp(
                        1 - cursorDistance / 210,
                        0,
                        1
                    );

                    const opacity =
                        (1 - distance / connectionDistance) *
                        (0.17 + hoverBoost * 0.43);

                    ctx.beginPath();
                    ctx.moveTo(a.x, a.y);
                    ctx.lineTo(b.x, b.y);

                    ctx.strokeStyle =
                        `rgba(52, 220, 222, ${opacity})`;

                    ctx.lineWidth = hoverBoost > 0.2
                        ? 1.1
                        : 0.65;

                    ctx.stroke();

                    edges.push([i, j]);
                }
            }

            // Traveling data signals
            for (const pulse of pulses) {
                pulse.progress += pulse.speed * dt;

                if (pulse.progress >= 1) {
                    pulse.progress = 0;

                    if (edges.length) {
                        const edge = edges[
                            Math.floor(Math.random() * edges.length)
                        ];

                        pulse.from = edge[0];
                        pulse.to = edge[1];
                    }
                }

                const a = positions[pulse.from];
                const b = positions[pulse.to];

                if (!a || !b) continue;

                const distance = Math.hypot(
                    a.x - b.x,
                    a.y - b.y
                );

                if (distance > connectionDistance) continue;

                const x =
                    a.x + (b.x - a.x) * pulse.progress;

                const y =
                    a.y + (b.y - a.y) * pulse.progress;

                const radius = 2.3;

                const glow = ctx.createRadialGradient(
                    x, y, 0,
                    x, y, 14
                );

                glow.addColorStop(
                    0,
                    "rgba(190, 255, 247, 0.85)"
                );

                glow.addColorStop(
                    0.25,
                    "rgba(38, 241, 213, 0.35)"
                );

                glow.addColorStop(
                    1,
                    "rgba(38, 241, 213, 0)"
                );

                ctx.fillStyle = glow;
                ctx.beginPath();
                ctx.arc(x, y, 14, 0, Math.PI * 2);
                ctx.fill();

                ctx.fillStyle = "#d4fff8";
                ctx.beginPath();
                ctx.arc(x, y, radius, 0, Math.PI * 2);
                ctx.fill();
            }

            // Network nodes
            positions.forEach(node => {
                const distance = pointer.active
                    ? Math.hypot(
                        node.x - pointer.x,
                        node.y - pointer.y
                    )
                    : Infinity;

                const hover = clamp(
                    1 - distance / 170,
                    0,
                    1
                );

                ctx.globalAlpha = 0.45 + hover * 0.5;
                ctx.fillStyle = node.color;

                ctx.shadowColor = node.color;
                ctx.shadowBlur = 5 + hover * 15;

                ctx.beginPath();
                ctx.arc(
                    node.x,
                    node.y,
                    node.radius * (1 + hover * 0.7),
                    0,
                    Math.PI * 2
                );
                ctx.fill();

                ctx.shadowBlur = 0;
                ctx.globalAlpha = 1;
            });
        };

        const animate = (timestamp) => {
            if (!active) return;

            raf = window.requestAnimationFrame(animate);

            const interval = 1000 / maxFps;

            if (timestamp - lastFrame < interval) return;

            const dt = lastFrame
                ? Math.min(
                    (timestamp - lastFrame) / 1000,
                    0.05
                )
                : 0;

            lastFrame = timestamp;
            elapsed += dt;

            draw(elapsed, dt);
        };

        const stop = () => {
            active = false;

            if (raf) {
                window.cancelAnimationFrame(raf);
                raf = 0;
            }

            lastFrame = 0;
        };

        const sync = () => {
            const allowed =
                desktopQuery.matches &&
                !motionQuery.matches;

            root.style.display = allowed
                ? "block"
                : "none";

            if (allowed && visible) {
                resize();

                if (!active) {
                    active = true;
                    raf = window.requestAnimationFrame(animate);
                }
            } else {
                stop();
            }
        };

        const handlePointerMove = (event) => {
            if (event.pointerType === "touch") return;

            const rect = root.getBoundingClientRect();

            pointer.x = event.clientX - rect.left;
            pointer.y = event.clientY - rect.top;
            pointer.active = true;
        };

        const handlePointerLeave = () => {
            pointer.active = false;
        };

        const handleVisibility = () => {
            sync();
        };

        const resizeObserver = new ResizeObserver(resize);

        const intersectionObserver = new IntersectionObserver(
            entries => {
                visible = entries[0]?.isIntersecting ?? false;
                sync();
            },
            { threshold: 0.01 }
        );

        createNetwork();
        resize();

        resizeObserver.observe(root);
        intersectionObserver.observe(root);

        hero.addEventListener(
            "pointermove",
            handlePointerMove,
            { passive: true }
        );

        hero.addEventListener(
            "pointerleave",
            handlePointerLeave
        );

        desktopQuery.addEventListener("change", sync);
        motionQuery.addEventListener("change", sync);

        document.addEventListener(
            "visibilitychange",
            handleVisibility
        );

        sync();

        return () => {
            stop();

            resizeObserver.disconnect();
            intersectionObserver.disconnect();

            hero.removeEventListener(
                "pointermove",
                handlePointerMove
            );

            hero.removeEventListener(
                "pointerleave",
                handlePointerLeave
            );

            desktopQuery.removeEventListener(
                "change",
                sync
            );

            motionQuery.removeEventListener(
                "change",
                sync
            );

            document.removeEventListener(
                "visibilitychange",
                handleVisibility
            );
        };
    }, [
        primary,
        secondary,
        accent,
        nodeCount,
        connectionDistance,
        pulseCount,
        maxFps,
        breakpoint
    ]);

    return (
        <div
            ref={rootRef}
            className={`es-infrastructure-field ${className}`}
            style={{
                "--es-field-primary": primary,
                "--es-field-secondary": secondary,
                "--es-field-accent": accent
            }}
            aria-hidden="true"
        >
            <div className="es-infrastructure-field__aurora" />
            <div className="es-infrastructure-field__grid" />
            <canvas ref={canvasRef} />
            <div className="es-infrastructure-field__vignette" />
        </div>
    );
}
